/**
 * Opportunity Detail Modal
 * Full-screen modal with tabbed interface for opportunity details
 */

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Card, Button, Badge, Tabs, formatCurrency } from '@tracekit/core';
import { useQuery } from '@tanstack/react-query';
import { getAPIClient } from '@tracekit/core';
import {
  X,
  Maximize2,
  Minimize2,
  ExternalLink,
  FileText,
  Calendar,
  DollarSign,
  MapPin,
  Building2,
  Tag,
  Star,
  Clock,
  AlertTriangle,
  Search,
  ChevronDown,
  ChevronRight,
  Brain,
  Zap,
  Info,
} from 'lucide-react';
import { GovtOpportunityEntity } from '../../pages/government-data/OpportunitiesDashboard';
import {
  enhanceOpportunityEntity,
  getDeadlineUrgency,
  getOpportunityValue,
  getProjectIdDisplay,
  getStageBadgeColor,
} from '../../utils/opportunityUtils';
import { ProposalSummaryCard } from '../ProposalSummaryCard';
import { RequirementsTab } from '../RequirementsTab';
import { OpportunityDocumentsTab } from './OpportunityDocumentsTab';
import { OpportunityEmailDraftTab } from './OpportunityEmailDraftTab';
import { useOpportunityAnalysis } from '../../hooks/useOpportunityAnalysis';
import { useWorkspace } from '@tracekit/core';
import { MatchScoreTooltip, type MatchExplain } from './MatchScoreTooltip';

interface OpportunityDetailModalProps {
  opportunity: GovtOpportunityEntity | null;
  isOpen: boolean;
  onClose: () => void;
  onSaveToList?: (listId: string) => void;
  onGenerateProposal?: () => void;
  onUpdateTracking?: (entityId: string, trackingStatus: string) => void; // For tracking status updates
  onOpportunityUpdate?: (updatedOpportunity: GovtOpportunityEntity) => void; // Callback to update opportunity in parent
}

export function OpportunityDetailModal({
  opportunity,
  isOpen,
  onClose,
  onSaveToList,
  onGenerateProposal,
  onUpdateTracking,
  onOpportunityUpdate,
}: OpportunityDetailModalProps) {
  const [isFullscreen, setIsFullscreen] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'requirements' | 'analysis' | 'documents' | 'email-draft'>('overview');
  const [expandedCitation, setExpandedCitation] = useState<number | null>(null);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const [analysisStage, setAnalysisStage] = useState<'idle' | 'retrieval' | 'thinking' | 'generating' | 'complete'>('idle');
  const [analysisElapsed, setAnalysisElapsed] = useState(0);
  const [showDetailModeWarning, setShowDetailModeWarning] = useState(false);
  const [forceRefreshAnalysis, setForceRefreshAnalysis] = useState(false);
  /** Ref for analysis tab content; used to delegate clicks on [Source N] links and scroll to Evidence Source cards */
  const analysisContentRef = useRef<HTMLDivElement>(null);
  /** Once user opens Analysis tab for an opportunity, we keep the query enabled so cache is used when switching tabs or coming back */
  const viewedAnalysisOpportunityIdsRef = useRef<Set<string>>(new Set());
  /** Persist last active tab per opportunity so reopening the same opportunity restores the tab (no "starting from scratch") */
  const lastActiveTabByOpportunityIdRef = useRef<Map<string, string>>(new Map());
  const prevIsOpenRef = useRef(false);
  const prevOpportunityIdRef = useRef<string | null>(null);
  const api = getAPIClient();

  // Loading messages that rotate during analysis
  const loadingMessages = [
    '🔍 Searching through knowledge base...',
    '📚 Analyzing documents...',
    '🧠 Processing with AI...',
    '💡 Generating insights...',
    '✨ Almost there...',
    '🎯 Finding the best evidence...',
  ];

  // Debug logging
  useEffect(() => {
    console.log('[OpportunityDetailModal] Props changed:', {
      isOpen,
      hasOpportunity: !!opportunity,
      opportunityId: opportunity?.id,
      opportunityTitle: opportunity?.data?.title,
    });
  }, [isOpen, opportunity]);

  // Open in fullscreen by default each time modal is opened.
  useEffect(() => {
    if (isOpen) setIsFullscreen(true);
  }, [isOpen]);

  const { projectId } = useWorkspace();

  // Fetch associated proposals for this opportunity
  const { data: proposalsData } = useQuery({
    queryKey: ['opportunity-proposals', opportunity?.id, projectId],
    queryFn: async () => {
      if (!opportunity?.id || !projectId) return null;
      try {
        const data = await api.get<{ opportunity_id: string; proposals: any[]; total: number }>(
          `/api/opportunities/${opportunity.id}/proposals`,
          { params: { project_id: projectId } }
        );
        return data;
      } catch (error) {
        console.error('[OpportunityDetailModal] Failed to fetch proposals:', error);
        return null;
      }
    },
    enabled: !!opportunity?.id && !!projectId,
  });

  // Restore last active tab when opening modal or switching opportunity (avoids "starting from scratch")
  useEffect(() => {
    if (!isOpen || !opportunity?.id) return;
    const justOpened = isOpen && !prevIsOpenRef.current;
    const opportunityIdChanged = opportunity.id !== prevOpportunityIdRef.current;
    prevIsOpenRef.current = isOpen;
    prevOpportunityIdRef.current = opportunity.id;
    if (justOpened || opportunityIdChanged) {
      const stored = lastActiveTabByOpportunityIdRef.current.get(opportunity.id);
      if (stored && (stored === 'overview' || stored === 'requirements' || stored === 'analysis' || stored === 'documents')) {
        setActiveTab(stored);
      }
    }
  }, [isOpen, opportunity?.id]);

  // Persist active tab per opportunity
  useEffect(() => {
    if (opportunity?.id) {
      lastActiveTabByOpportunityIdRef.current.set(opportunity.id, activeTab);
    }
  }, [opportunity?.id, activeTab]);

  // Mark that user has viewed Analysis for this opportunity so we keep the query enabled (cache stays)
  useEffect(() => {
    if (activeTab === 'analysis' && opportunity?.id) {
      viewedAnalysisOpportunityIdsRef.current.add(opportunity.id);
    }
  }, [activeTab, opportunity?.id]);

  // Fetch analysis when Analysis tab is opened; keep query enabled once viewed so tab/path change doesn't lose cache
  const analysisQueryEnabled =
    isOpen &&
    !!opportunity?.id &&
    (activeTab === 'analysis' || viewedAnalysisOpportunityIdsRef.current.has(opportunity.id));
  const {
    data: analysis,
    isLoading: analysisLoading,
    isError: analysisError,
    error: analysisErrorDetail,
  } = useOpportunityAnalysis(
    opportunity?.id || null,
    projectId || undefined,
    {
      include_technical_approaches: true,
      include_past_projects: true,
      include_staff_allocation: true,
      include_resource_estimation: true,
    },
    analysisQueryEnabled,
    'quick',  // analysis_mode - default to quick
    forceRefreshAnalysis  // force_refresh - pass state from re-analyze buttons
  );

  // Track analysis loading stages and elapsed time
  useEffect(() => {
    if (analysisLoading) {
      setAnalysisStage('retrieval');
      const startTime = Date.now();
      const interval = setInterval(() => {
        setAnalysisElapsed((Date.now() - startTime) / 1000);
      }, 100);

      // Simulate stage progression
      const stageTimeout = setTimeout(() => setAnalysisStage('thinking'), 2000);
      const generatingTimeout = setTimeout(() => setAnalysisStage('generating'), 4000);

      return () => {
        clearInterval(interval);
        clearTimeout(stageTimeout);
        clearTimeout(generatingTimeout);
      };
    } else if (analysis) {
      setAnalysisStage('complete');
      // Reset force_refresh after analysis completes
      setForceRefreshAnalysis(false);
    } else {
      setAnalysisStage('idle');
      setAnalysisElapsed(0);
      setForceRefreshAnalysis(false);
    }
  }, [analysisLoading, analysis]);

  // Rotate loading messages while analyzing
  useEffect(() => {
    if (!analysisLoading) return;

    const interval = setInterval(() => {
      setLoadingMessageIndex((prev) => (prev + 1) % loadingMessages.length);
    }, 2000); // Change message every 2 seconds

    return () => clearInterval(interval);
  }, [analysisLoading, loadingMessages.length]);

  // Handle re-analyze in quick mode
  const handleReanalyzeQuick = async () => {
    setForceRefreshAnalysis(true);
    // The hook will now see force_refresh=true and fetch fresh data
    // Once the query completes, we can reset it
  };

  // Handle re-analyze in detail mode (after warning confirmation)
  const handleReanalyzeDetail = async () => {
    setShowDetailModeWarning(false);
    setForceRefreshAnalysis(true);
    // The hook will now see force_refresh=true and fetch fresh data with deep analysis
    // Once the query completes, we can reset it
  };

  /**
   * Returns HTML for a clickable [Source N] pill. Click is handled by delegation on analysisContentRef;
   * scroll target is the Evidence Source card with id="evidence-source-{num}".
   */
  const getClickableSourcePillHtml = (num: string) =>
    `<span class="text-xs text-blue-600 dark:text-blue-400 font-medium cursor-pointer hover:underline focus:outline-none rounded px-0.5 opportunity-evidence-source-link" data-evidence-source-index="${num}" role="button" tabindex="0" title="Scroll to Evidence Source ${num}">[Source ${num}]</span>`;

  // Convert markdown to HTML for proper formatting with nested list support
  const convertMarkdownToHTML = (markdown: string): string => {
    if (!markdown) return '';

    // Remove extra metadata text at the top (like "{model_version} documents")
    let cleaned = markdown.replace(/^\{[^}]+\}\s*documents?\s*/i, '').trim();

    // Process line by line to handle lists and nested lists properly
    const lines = cleaned.split('\n');
    const processedBlocks: string[] = [];
    let currentList: { type: 'ul' | 'ol'; items: string[]; indent: number } | null = null;
    let currentParagraph: string[] = [];

    const flushParagraph = () => {
      if (currentParagraph.length > 0) {
        const paraText = currentParagraph.join(' ').trim();
        if (paraText) {
          processedBlocks.push(`<p class="mb-3 text-gray-700 dark:text-slate-300">${paraText}</p>`);
        }
        currentParagraph = [];
      }
    };

    const flushList = () => {
      if (currentList && currentList.items.length > 0) {
        const listTag = currentList.type;
        const indentClass = currentList.indent > 0 ? `ml-${currentList.indent * 4}` : 'ml-4';
        processedBlocks.push(`<${listTag} class="list-disc list-inside space-y-1 my-2 ${indentClass}">${currentList.items.join('')}</${listTag}>`);
        currentList = null;
      }
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmed = line.trim();

      if (!trimmed) {
        flushParagraph();
        flushList();
        continue;
      }

      // Check for numbered list (1. item)
      const numberedMatch = trimmed.match(/^(\d+)\.\s+(.+)$/);
      // Check for bullet list (- item or * item)
      const bulletMatch = trimmed.match(/^[-*•]\s+(.+)$/);
      // Check for nested bullet (indented with spaces or tabs)
      const indentMatch = line.match(/^(\s+)([-*•]|\d+\.)\s+(.+)$/);

      if (indentMatch) {
        // Nested list item
        const indentLevel = Math.floor(indentMatch[1].replace(/\t/g, '    ').length / 4);
        const isNumbered = /^\d+\./.test(indentMatch[2]);
        const content = indentMatch[3];

        flushParagraph();

        if (!currentList || currentList.indent !== indentLevel ||
          (isNumbered && currentList.type !== 'ol') ||
          (!isNumbered && currentList.type !== 'ul')) {
          flushList();
          currentList = {
            type: isNumbered ? 'ol' : 'ul',
            items: [],
            indent: indentLevel
          };
        }

        let itemContent = content;
        // Process inline formatting; [Source 1, Source 2, ...] and [Source N, M] → clickable pills
        itemContent = itemContent.replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold text-theme-text">$1</strong>');
        itemContent = itemContent.replace(/\[Source\s+\d+(?:\s*,\s*Source\s+\d+)*\]/gi, (match: string) =>
          (match.match(/\d+/g) ?? []).map((num) => getClickableSourcePillHtml(num)).join(', ')
        );
        itemContent = itemContent.replace(/\[Source\s+([\d\s,]+)\]/gi, (_: string, nums: string) =>
          nums.split(',').map((n) => n.trim()).filter(Boolean).map((num) => getClickableSourcePillHtml(num)).join(', ')
        );
        currentList.items.push(`<li class="mb-1">${itemContent}</li>`);
      } else if (numberedMatch) {
        flushParagraph();
        if (!currentList || currentList.type !== 'ol' || currentList.indent !== 0) {
          flushList();
          currentList = { type: 'ol', items: [], indent: 0 };
        }
        let itemContent = numberedMatch[2];
        itemContent = itemContent.replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold text-theme-text">$1</strong>');
        itemContent = itemContent.replace(/\[Source\s+\d+(?:\s*,\s*Source\s+\d+)*\]/gi, (match: string) =>
          (match.match(/\d+/g) ?? []).map((num) => getClickableSourcePillHtml(num)).join(', ')
        );
        itemContent = itemContent.replace(/\[Source\s+([\d\s,]+)\]/gi, (_: string, nums: string) =>
          nums.split(',').map((n) => n.trim()).filter(Boolean).map((num) => getClickableSourcePillHtml(num)).join(', ')
        );
        currentList.items.push(`<li class="mb-1">${itemContent}</li>`);
      } else if (bulletMatch) {
        flushParagraph();
        if (!currentList || currentList.type !== 'ul' || currentList.indent !== 0) {
          flushList();
          currentList = { type: 'ul', items: [], indent: 0 };
        }
        let itemContent = bulletMatch[1];
        itemContent = itemContent.replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold text-theme-text">$1</strong>');
        itemContent = itemContent.replace(/\[Source\s+\d+(?:\s*,\s*Source\s+\d+)*\]/gi, (match: string) =>
          (match.match(/\d+/g) ?? []).map((num) => getClickableSourcePillHtml(num)).join(', ')
        );
        itemContent = itemContent.replace(/\[Source\s+([\d\s,]+)\]/gi, (_: string, nums: string) =>
          nums.split(',').map((n) => n.trim()).filter(Boolean).map((num) => getClickableSourcePillHtml(num)).join(', ')
        );
        currentList.items.push(`<li class="mb-1">${itemContent}</li>`);
      } else {
        // Regular paragraph text
        flushList();
        currentParagraph.push(trimmed);
      }
    }

    flushParagraph();
    flushList();

    let html = processedBlocks.join('');

    // Process inline formatting for paragraphs (bold, italic, links, citations)
    html = html.replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold text-theme-text">$1</strong>');
    html = html.replace(/__([^_]+)__/g, '<strong class="font-semibold text-theme-text">$1</strong>');
    html = html.replace(/\*([^*]+)\*/g, '<em class="italic text-gray-700 dark:text-gray-300">$1</em>');
    html = html.replace(/_([^_]+)_/g, '<em class="italic text-gray-700 dark:text-gray-300">$1</em>');
    // [Source 1, Source 2, Source 3, ...] (repeated "Source N" inside one bracket) → clickable pills
    html = html.replace(/\[Source\s+\d+(?:\s*,\s*Source\s+\d+)*\]/gi, (match: string) => {
      const numbers = (match.match(/\d+/g) ?? []).filter(Boolean);
      return numbers.map((num) => getClickableSourcePillHtml(num)).join(', ');
    });
    // [Source N] and [Source N, M, ...] (numbers only, e.g. [Source 1, 2, 3]) → clickable pills
    html = html.replace(/\[Source\s+([\d\s,]+)\]/gi, (_: string, nums: string) => {
      const numbers = nums.split(',').map((n) => n.trim()).filter(Boolean);
      return numbers.map((num) => getClickableSourcePillHtml(num)).join(', ');
    });
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline" target="_blank" rel="noopener noreferrer">$1</a>');

    // Headers
    html = html.replace(/<p[^>]*>### (.+?)<\/p>/g, '<h3 class="text-base font-semibold text-theme-text mt-4 mb-2">$1</h3>');
    html = html.replace(/<p[^>]*>## (.+?)<\/p>/g, '<h2 class="text-lg font-bold text-theme-text mt-5 mb-3">$1</h2>');
    html = html.replace(/<p[^>]*># (.+?)<\/p>/g, '<h1 class="text-xl font-bold text-theme-text mt-6 mb-4">$1</h1>');

    return html || '<p class="text-gray-700 dark:text-slate-300">No content available.</p>';
  };

  useEffect(() => {
    if (!isOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    // Close on Escape key
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  /**
   * Delegate clicks on [Source N] pills (rendered with data-evidence-source-index) to scroll
   * the corresponding Evidence Source card into view. Pills are inside dangerouslySetInnerHTML
   * so we use a single listener on the analysis content container.
   */
  useEffect(() => {
    const container = analysisContentRef.current;
    if (!container) return;
    const handleClick = (e: MouseEvent) => {
      const target = (e.target as HTMLElement).closest?.('[data-evidence-source-index]');
      if (!target) return;
      const index = (target as HTMLElement).getAttribute('data-evidence-source-index');
      if (!index) return;
      e.preventDefault();
      const el = document.getElementById(`evidence-source-${index}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    container.addEventListener('click', handleClick);
    return () => container.removeEventListener('click', handleClick);
  }, [analysis]); // Re-attach when analysis content changes (e.g. new analysis loaded)

  if (!isOpen || !opportunity) {
    console.log('[OpportunityDetailModal] Not rendering:', { isOpen, hasOpportunity: !!opportunity });
    return null;
  }

  console.log('[OpportunityDetailModal] Rendering modal for:', opportunity.id);

  // Enhance opportunity with computed fields
  const enhancedOpp = enhanceOpportunityEntity(opportunity);
  const opp = enhancedOpp.data;
  const rawData = opp.raw_data as Record<string, unknown> | undefined;
  const viewUrlRaw = opp.url || (rawData && rawData.detail_url);
  const viewUrl = typeof viewUrlRaw === 'string' && (viewUrlRaw.startsWith('http://') || viewUrlRaw.startsWith('https://')) ? viewUrlRaw : null;
  const requiresPortalLogin = Boolean(
    (opp as { requires_portal_login?: boolean }).requires_portal_login
    || rawData?.requires_portal_login
    || (String(opp.source || '').toLowerCase().includes('tender247') && !rawData?.security_code)
  );
  const daysUntil = opp.days_until_deadline ?? null;
  const urgency = getDeadlineUrgency(daysUntil);
  const isUrgent = urgency === 'urgent';
  const isExpired = urgency === 'expired';
  const isSoon = urgency === 'soon';

  // Get key stats
  const opportunityValue = getOpportunityValue(opp);
  const projectIdDisplay = getProjectIdDisplay(enhancedOpp);
  const stage = opp.proposal_stage || 'Unknown';
  const duration = opp.duration_display || 'Not specified';

  // Get current tracking status
  const currentTrackingStatus = opportunity?.data?.tracking_status || '';

  // Get button text and styling based on tracking status
  const getTrackingButtonConfig = () => {
    switch (currentTrackingStatus) {
      case 'watching':
        return {
          text: 'Watching',
          className: 'bg-blue-50 dark:bg-blue-900/20 border-blue-600 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/30',
        };
      case 'bidding':
        return {
          text: 'Bidding',
          className: 'bg-green-50 dark:bg-green-900/20 border-green-600 text-green-700 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-900/30',
        };
      case 'won':
        return {
          text: 'Won',
          className: 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-600 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/30',
        };
      case 'lost':
        return {
          text: 'Lost',
          className: 'bg-red-50 dark:bg-red-900/20 border-red-600 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/30',
        };
      case 'declined':
        return {
          text: 'Declined',
          className: 'bg-gray-50 dark:bg-gray-800/50 border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800',
        };
      default:
        return {
          text: 'Not Tracking',
          className: '',
        };
    }
  };

  const trackingButtonConfig = getTrackingButtonConfig();

  /** Above app shell; backdrop is fixed to viewport so scrolling never reveals the page behind. */
  const MODAL_Z = 10050;

  /** Opaque surfaces (do not rely on theme CSS vars alone — avoids light-mode regressions / see-through). */
  const shellBg = 'bg-white dark:bg-slate-900';
  const shellBorder = 'border border-slate-200 dark:border-slate-600';

  const modalContent = (
    <div
      className="fixed inset-0 overflow-hidden overscroll-contain"
      style={{ zIndex: MODAL_Z }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="opportunity-detail-modal-title"
    >
      {/* Fixed to viewport — not a child of a scrolling box (absolute + scroll parent caused bleed-through). */}
      <button
        type="button"
        className="fixed inset-0 z-0 block cursor-default border-0 bg-slate-900/60 p-0 transition-opacity dark:bg-black/70"
        aria-label="Close opportunity details"
        onClick={onClose}
      />

      <div
        className={`pointer-events-none absolute inset-0 z-10 flex items-center justify-center ${
          isFullscreen ? 'p-0' : 'p-4 sm:p-6'
        }`}
      >
        <div
          className={`pointer-events-auto relative flex flex-col overflow-hidden shadow-2xl ${shellBg} ${shellBorder} ${
            isFullscreen
              ? 'h-[100dvh] w-[100vw] max-h-[100dvh] max-w-[100vw] rounded-none border-0'
              : 'max-h-[90vh] w-full max-w-6xl rounded-xl'
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Solid header — no transparent gradient stops */}
          <div className={`border-b border-slate-200 px-6 py-3 dark:border-slate-600 ${shellBg}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <h2 id="opportunity-detail-modal-title" className="text-xl font-bold text-theme-text mb-1.5 line-clamp-2">
                  {opp.title}
                </h2>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-1.5 text-xs text-gray-600 dark:!text-slate-100">
                    <Building2 className="h-3.5 w-3.5 flex-shrink-0 dark:!text-slate-100" />
                    <span className="truncate">{opp.organization || opp.issuing_agency || 'Unknown'}</span>
                  </div>
                  {opp.source && (
                    <Badge variant="default" className="text-xs py-0.5 px-2 ">
                      <Tag className="h-3 w-3 mr-1 dark:!text-white" />
                      {opp.source}
                    </Badge>
                  )}
                  {opp.status && (
                    <Badge
                      variant={
                        opp.status === 'open' ? 'success' : opp.status === 'closed' ? 'default' : 'warning'
                      }
                      className="text-xs py-0.5 px-2 "
                    >
                      {opp.status}
                    </Badge>
                  )}
                  {opp.match_score !== undefined && opp.match_score !== null && opp.match_score > 0 && (
                    <MatchScoreTooltip
                      score={opp.match_score}
                      variant="badge"
                      explain={
                        ((opp as { match_explain?: MatchExplain }).match_explain ??
                          (opp as { data?: { match_explain?: MatchExplain } }).data?.match_explain) ??
                        null
                      }
                    />
                  )}
                </div>
              </div>
              <div className="ml-2 flex items-center gap-1.5 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setIsFullscreen((v) => !v)}
                  className="p-1.5 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                  title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                  aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                >
                  {isFullscreen ? (
                    <Minimize2 className="h-5 w-5 text-gray-500 dark:!text-slate-200" />
                  ) : (
                    <Maximize2 className="h-5 w-5 text-gray-500 dark:!text-slate-200" />
                  )}
                </button>
                <button
                  onClick={onClose}
                  className="p-1.5 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                  title="Close"
                  aria-label="Close"
                >
                  <X className="h-5 w-5 text-gray-500 dark:!text-slate-200" />
                </button>
              </div>
            </div>
          </div>

          {/* Comprehensive Stats Section - Condensed */}
          <div className={`border-b border-slate-200 px-6 py-3 dark:border-slate-600 ${shellBg}`}>
            <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-3">
              {/* Project ID - Span 2 columns to use more space */}
              <div className="flex flex-col col-span-2 min-w-0">
                <span className="text-xs font-medium text-gray-500 dark:text-slate-200 mb-0.5">Project ID</span>
                <span
                  className="text-xs font-mono font-semibold text-theme-text break-all"
                  title={projectIdDisplay}
                >
                  {projectIdDisplay}
                </span>
              </div>

              {/* Proposal Stage */}
              {stage !== 'Unknown' && (
                <div className="flex flex-col">
                  <span className="text-xs font-medium text-gray-500 dark:text-slate-200 mb-0.5">Stage</span>
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium border w-fit ${getStageBadgeColor(stage)}`}>
                    {stage}
                  </span>
                </div>
              )}

              {/* Dollar Value */}
              {opportunityValue !== null && (
                <div className="flex flex-col">
                  <span className="text-xs font-medium text-gray-500 dark:text-slate-200 mb-0.5">Value</span>
                  <div className="flex items-center gap-1">
                    <DollarSign className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
                    <span className="text-xs font-semibold text-green-700 dark:text-green-300">
                      {formatCurrency(opportunityValue, { compact: true, symbol: opp.currency === 'CAD' ? 'C$' : '$' })}
                    </span>
                  </div>
                </div>
              )}

              {/* Duration */}
              {duration !== 'Not specified' && (
                <div className="flex flex-col">
                  <span className="text-xs font-medium text-gray-500 dark:text-slate-200 mb-0.5">Duration</span>
                  <div className="flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5 text-gray-600 dark:text-slate-300" />
                    <span className="text-xs font-medium text-theme-text">{duration}</span>
                  </div>
                </div>
              )}

              {/* Deadline */}
              {opp.deadline && (
                <div className="flex flex-col">
                  <span className="text-xs font-medium text-gray-500 dark:text-slate-200 mb-0.5">Deadline</span>
                  <div className="flex items-center gap-1">
                    <Calendar className={`h-3.5 w-3.5 ${isUrgent ? 'text-red-600 dark:text-red-400' :
                        isExpired ? 'text-gray-400 dark:text-slate-400' :
                          isSoon ? 'text-yellow-600 dark:text-yellow-400' :
                            'text-blue-600 dark:text-blue-400'
                      }`} />
                    <div className="flex flex-col">
                      <span className={`text-xs font-semibold ${isUrgent ? 'text-red-700 dark:text-red-300' :
                          isExpired ? 'text-gray-500 dark:text-gray-400' :
                            isSoon ? 'text-yellow-700 dark:text-yellow-300' :
                              'text-blue-700 dark:text-blue-300'
                        }`}>
                        {new Date(opp.deadline).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </span>
                      <span className={`text-xs ${isUrgent ? 'text-red-600 dark:text-red-400' :
                          isExpired ? 'text-gray-500 dark:text-gray-400' :
                            isSoon ? 'text-yellow-600 dark:text-yellow-400' :
                              'text-blue-600 dark:text-blue-400'
                        }`}>
                        {isExpired
                          ? `Expired ${Math.abs(daysUntil!)} days ago`
                          : daysUntil === 0
                            ? 'Due today!'
                            : daysUntil === 1
                              ? '1 day left'
                              : `${daysUntil} days left`}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Match Score */}
              {opp.match_score !== undefined && opp.match_score !== null && opp.match_score > 0 && (
                <div className="flex flex-col">
                  <span className="text-xs font-medium text-gray-500 dark:text-slate-200 mb-1">Match Score</span>
                  <MatchScoreTooltip
                    score={opp.match_score}
                    variant="detail"
                    explain={
                      ((opp as { match_explain?: MatchExplain }).match_explain ??
                        (opp as { data?: { match_explain?: MatchExplain } }).data?.match_explain) ??
                      null
                    }
                  />
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions – solid tint (no /50 alpha — was compositing with page behind in some themes) */}
          <div className="border-b border-slate-200 bg-sky-50 px-6 py-4 dark:border-slate-600 dark:bg-slate-800">
            <div className="flex items-center gap-3 flex-wrap">
              {viewUrl && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => window.open(viewUrl, '_blank')}
                  className="ring-2 ring-primary-200 dark:ring-primary-700"
                  title="Open the official RFP posting to find and download documents"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  View original source
                </Button>
              )}
              {requiresPortalLogin && (
                <Badge variant="warning" className="text-xs">
                  Portal login may be required to download documents
                </Badge>
              )}
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700 dark:text-slate-200 whitespace-nowrap" title="Internal workflow stage (e.g. Watching, Bidding, Won, Lost)">
                  Opportunity Stage:
                </label>
                <select
                  value={currentTrackingStatus}
                  onChange={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    const newStatus = e.target.value;
                    if (onUpdateTracking && opportunity) {
                      onUpdateTracking(opportunity.id, newStatus);
                      // Optimistically update the local opportunity state
                      if (onOpportunityUpdate && opportunity) {
                        onOpportunityUpdate({
                          ...opportunity,
                          data: {
                            ...opportunity.data,
                            tracking_status: newStatus === '' ? undefined : (newStatus as any),
                          },
                        });
                      }
                    }
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  className={`min-w-[140px] rounded border border-slate-300 bg-white px-2 py-1 text-sm text-theme-text transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-slate-600 dark:bg-slate-900 ${currentTrackingStatus === 'watching'
                      ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-600 text-blue-700 dark:text-blue-300'
                      : currentTrackingStatus === 'bidding'
                        ? 'bg-green-50 dark:bg-green-900/20 border-green-600 text-green-700 dark:text-green-300'
                        : currentTrackingStatus === 'won'
                          ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-600 text-emerald-700 dark:text-emerald-300'
                          : currentTrackingStatus === 'lost'
                            ? 'bg-red-50 dark:bg-red-900/20 border-red-600 text-red-700 dark:text-red-300'
                            : currentTrackingStatus === 'declined'
                              ? 'bg-gray-50 dark:bg-gray-800/50 border-gray-600 text-gray-700 dark:text-gray-300'
                              : ''
                    }`}
                >
                  <option value="">Not Tracking</option>
                  <option value="watching">Watching</option>
                  <option value="bidding">Bidding</option>
                  <option value="won">Won</option>
                  <option value="lost">Lost</option>
                  <option value="declined">Declined</option>
                </select>
              </div>
              <Button
                variant="primary"
                size="sm"
                onClick={onGenerateProposal}
                className="font-semibold ring-2 ring-primary-200 dark:ring-primary-700"
                title="Create a proposal from this opportunity (do this after uploading RFP docs and drafting the email)"
              >
                <FileText className="h-4 w-4 mr-2" />
                Generate Proposal
              </Button>
            </div>
          </div>

          {/* Tabs — min-h-0 required so flex child scrolls inside max-h panel instead of escaping over the page */}
          <div className={`min-h-0 flex-1 overflow-y-auto p-6 ${shellBg}`}>
            {/* Numbered flow: 1–5 match tab numbers; View original source and Generate Proposal are buttons above */}
            <div className="mb-4 rounded-lg border border-sky-200 bg-sky-50 p-3 dark:border-slate-600 dark:bg-slate-800">
              <p className="text-sm text-primary-800 dark:text-primary-200">
                <Info className="h-4 w-4 inline-block mr-2 align-middle" aria-hidden />
                <span className="font-medium">Recommended flow:</span>{' '}
                <strong>1.</strong> Overview → <strong>2.</strong> Requirements → <strong>3.</strong> Use <strong>View original source</strong> (button above) to open the RFP and find documents, then upload them in <strong>Additional Documents</strong> → <strong>4.</strong> <strong>Suitability Analysis</strong> → <strong>5.</strong> <strong>Email Draft</strong> → then click <strong>Generate Proposal</strong> when ready.
              </p>
            </div>
            <Tabs
              tabs={[
                {
                  id: 'overview',
                  label: '1. Overview',
                  title: 'Summary, description, deadline, and key details for this opportunity.',
                  content: (
                    <div className="space-y-6">
                      {/* Proposal AI Summary - Above Description */}
                      <ProposalSummaryCard opportunityId={opportunity.id} />

                      {/* Associated Proposals */}
                      {proposalsData && proposalsData.total > 0 && (
                        <Card className="p-4">
                          <h3 className="font-semibold mb-3 text-theme-text flex items-center gap-2">
                            <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                            Associated Proposals ({proposalsData.total})
                          </h3>
                          <div className="space-y-2">
                            {proposalsData.proposals.map((proposal: any) => (
                              <a
                                key={proposal.id}
                                href={`/rfp/proposals-management?tab=list&proposal_id=${proposal.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                              >
                                <div className="flex items-start justify-between gap-3">
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-theme-text truncate">
                                      {proposal.name}
                                    </p>
                                    <p className="text-sm text-gray-600 dark:text-slate-400 mt-1">
                                      {proposal.client_name}
                                    </p>
                                    <div className="flex items-center gap-3 mt-2">
                                      <Badge
                                        variant={
                                          proposal.status === 'completed'
                                            ? 'success'
                                            : proposal.status === 'in_progress'
                                              ? 'warning'
                                              : 'default'
                                        }
                                      >
                                        {proposal.status}
                                      </Badge>
                                      {proposal.created_at && (
                                        <span className="text-xs text-gray-500 dark:text-slate-400">
                                          Created {new Date(proposal.created_at).toLocaleDateString()}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <ExternalLink className="h-4 w-4 text-gray-400 flex-shrink-0 mt-1" />
                                </div>
                              </a>
                            ))}
                          </div>
                        </Card>
                      )}

                      {opp.description && (
                        <Card className="p-4">
                          <h3 className="font-semibold mb-2 text-theme-text">Description</h3>
                          <p className="text-gray-700 dark:text-slate-200 whitespace-pre-wrap">
                            {opp.description}
                          </p>
                        </Card>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {opp.value && (
                          <Card className="p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <DollarSign className="h-5 w-5 text-green-600 dark:text-green-400" />
                              <h3 className="font-semibold text-theme-text">Value</h3>
                            </div>
                            <p className="text-2xl font-bold text-green-700 dark:text-green-400">
                              {formatCurrency(opp.value, { compact: true })}
                            </p>
                          </Card>
                        )}

                        {opp.deadline && (
                          <Card className="p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <Clock
                                className={`h-5 w-5 ${isUrgent ? 'text-red-600 dark:text-red-400' : isExpired ? 'text-gray-400 dark:text-slate-400' : 'text-blue-600 dark:text-blue-400'
                                  }`}
                              />
                              <h3 className="font-semibold text-theme-text">Deadline</h3>
                            </div>
                            <p className="text-lg font-semibold text-theme-text">
                              {opp.deadline ? new Date(opp.deadline).toLocaleDateString() : 'N/A'}
                            </p>
                            {daysUntil !== null && (
                              <p
                                className={`text-sm mt-1 ${isUrgent
                                    ? 'text-red-600 font-semibold'
                                    : isExpired
                                      ? 'text-gray-500 dark:text-slate-400'
                                      : 'text-blue-600'
                                  }`}
                              >
                                {isExpired
                                  ? `${Math.abs(daysUntil)} days overdue`
                                  : daysUntil === 0
                                    ? 'Due today!'
                                    : `${daysUntil} days left`}
                              </p>
                            )}
                          </Card>
                        )}

                        {opp.location && (
                          <Card className="p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <MapPin className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                              <h3 className="font-semibold text-theme-text">Location</h3>
                            </div>
                            <p className="text-gray-700 dark:text-slate-200">{opp.location}</p>
                          </Card>
                        )}

                        {opp.posted_date && (
                          <Card className="p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <Calendar className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                              <h3 className="font-semibold text-theme-text">Posted Date</h3>
                            </div>
                            <p className="text-gray-700 dark:text-slate-200">
                              {opp.posted_date ? new Date(opp.posted_date).toLocaleDateString() : 'N/A'}
                            </p>
                          </Card>
                        )}
                      </div>

                      {opp.matching_service_lines && opp.matching_service_lines.length > 0 && (
                        <Card className="p-4">
                          <h3 className="font-semibold mb-3 text-theme-text">Service Lines</h3>
                          <div className="flex flex-wrap gap-2">
                            {opp.matching_service_lines.map((line) => (
                              <Badge key={line} variant="default" className="bg-blue-100 dark:bg-blue-900/20">
                                {line}
                              </Badge>
                            ))}
                          </div>
                        </Card>
                      )}

                      {opp.contact_info && (
                        <Card className="p-4">
                          <h3 className="font-semibold mb-2 text-theme-text">Contact Information</h3>
                          <p className="text-gray-700 dark:text-slate-200 whitespace-pre-wrap">
                            {opp.contact_info}
                          </p>
                        </Card>
                      )}
                    </div>
                  ),
                },
                {
                  id: 'requirements',
                  label: '2. Requirements',
                  title: 'Mandatory and evaluation requirements extracted from the RFP.',
                  content: (
                    <RequirementsTab opportunityId={opportunity.id} />
                  ),
                },
                {
                  id: 'documents',
                  label: '3. Additional Documents',
                  title: 'Upload RFP documents and supporting files here first.',
                  content: (
                    <OpportunityDocumentsTab
                      opportunityId={opportunity.id}
                      projectId={projectId || ''}
                      currentTrackingStatus={enhancedOpp?.data?.tracking_status}
                      onTrackingStatusChanged={(newStatus) => {
                        if (opportunity && onOpportunityUpdate) {
                          const updated = {
                            ...opportunity,
                            data: {
                              ...opportunity.data,
                              tracking_status: newStatus,
                            },
                          };
                          onOpportunityUpdate(updated as GovtOpportunityEntity);
                        }
                      }}
                    />
                  ),
                },
                {
                  id: 'analysis',
                  label: '4. Suitability Analysis',
                  title: 'AI analysis of fit, capabilities, and recommended next steps.',
                  content: (
                    <div className="space-y-4">
                      {analysisLoading ? (
                        <>
                          <Card className="bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-200 dark:from-purple-900/20 dark:to-indigo-900/20 dark:border-purple-700">
                            {/* Title and Description */}
                            <div className="mb-6 pb-4 border-b border-purple-200 dark:border-purple-700">
                              <h2 className="text-xl font-bold text-purple-900 dark:text-purple-100 mb-1">Analyzing Opportunity</h2>
                              <p className="text-sm text-purple-700 dark:text-purple-300 mb-3">
                                Our AI is analyzing this opportunity against your company's capabilities, past projects, and team expertise.
                              </p>
                              <div className="grid grid-cols-2 gap-3 text-xs">
                                <div className="bg-theme-paper/50 rounded p-2 border border-purple-100 dark:border-purple-800">
                                  <p className="text-purple-600 dark:text-purple-400 font-semibold">Matching:</p>
                                  <p className="text-gray-600 dark:text-gray-400">Technical fit & capabilities</p>
                                </div>
                                <div className="bg-theme-paper/50 rounded p-2 border border-purple-100 dark:border-purple-800">
                                  <p className="text-purple-600 dark:text-purple-400 font-semibold">Allocating:</p>
                                  <p className="text-gray-600 dark:text-gray-400">Best suited team members</p>
                                </div>
                                <div className="bg-theme-paper/50 rounded p-2 border border-purple-100 dark:border-purple-800">
                                  <p className="text-purple-600 dark:text-purple-400 font-semibold">Reviewing:</p>
                                  <p className="text-gray-600 dark:text-gray-400">Past project relevance</p>
                                </div>
                                <div className="bg-theme-paper/50 rounded p-2 border border-purple-100 dark:border-purple-800">
                                  <p className="text-purple-600 dark:text-purple-400 font-semibold">Recommending:</p>
                                  <p className="text-gray-600 dark:text-gray-400">Strategic next steps</p>
                                </div>
                              </div>
                            </div>

                            {/* Enhanced Streaming status indicator with rotating messages */}
                            <div className="mb-4 p-4 bg-gradient-to-r from-purple-100 via-indigo-100 to-purple-100 dark:from-purple-800/30 dark:via-indigo-800/30 dark:to-purple-800/30 rounded-lg border-2 border-purple-300 dark:border-purple-600 shadow-lg">
                              <div className="flex items-center gap-3 mb-3">
                                <div className="relative">
                                  {analysisStage === 'retrieval' || analysisStage === 'idle' ? (
                                    <Search className="h-5 w-5 text-purple-600 dark:text-purple-400 animate-bounce" />
                                  ) : analysisStage === 'thinking' ? (
                                    <Brain className="h-5 w-5 text-purple-600 dark:text-purple-400 animate-pulse" />
                                  ) : (
                                    <Zap className="h-5 w-5 text-purple-600 dark:text-purple-400 animate-spin" />
                                  )}
                                  <span className="absolute -top-1 -right-1 flex h-3 w-3">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 dark:bg-purple-500 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-3 w-3 bg-purple-500 dark:bg-purple-400"></span>
                                  </span>
                                </div>
                                <div className="flex-1">
                                  <div className="text-sm font-semibold text-purple-900 dark:text-purple-100 mb-1">
                                    {analysisStage === 'retrieval' ? 'Searching knowledge base...' :
                                      analysisStage === 'thinking' ? 'Analyzing capabilities...' :
                                        analysisStage === 'generating' ? 'Generating insights...' :
                                          'Finalizing analysis...'}
                                  </div>
                                  <div className="text-xs text-purple-700 dark:text-purple-300 animate-pulse">
                                    {loadingMessages[loadingMessageIndex]}
                                  </div>
                                </div>
                                {analysisElapsed > 0 && (
                                  <div className="text-right">
                                    <div className="text-lg font-bold text-purple-600 dark:text-purple-400">{analysisElapsed.toFixed(1)}s</div>
                                    <div className="text-xs text-purple-500 dark:text-purple-400">elapsed</div>
                                  </div>
                                )}
                              </div>

                              {/* Enhanced Progress bar with stages */}
                              <div className="space-y-2">
                                <div className="flex justify-between text-xs text-purple-700 dark:text-purple-300 font-medium">
                                  <span className={analysisStage === 'retrieval' || analysisStage === 'idle' ? 'text-purple-900 dark:text-purple-100' : ''}>
                                    Search
                                  </span>
                                  <span className={analysisStage === 'thinking' ? 'text-purple-900 dark:text-purple-100' : ''}>
                                    Think
                                  </span>
                                  <span className={analysisStage === 'generating' ? 'text-purple-900 dark:text-purple-100' : ''}>
                                    Write
                                  </span>
                                  <span className={analysisStage === 'complete' ? 'text-purple-900 dark:text-purple-100' : ''}>
                                    Done
                                  </span>
                                </div>
                                <div className="h-2 bg-purple-200 dark:bg-purple-800 rounded-full overflow-hidden shadow-inner">
                                  <div
                                    className="h-full bg-gradient-to-r from-purple-500 via-indigo-500 to-purple-600 dark:from-purple-400 dark:via-indigo-400 dark:to-purple-500 transition-all duration-500 ease-out relative overflow-hidden"
                                    style={{
                                      width: analysisStage === 'retrieval' || analysisStage === 'idle' ? '25%' :
                                        analysisStage === 'thinking' ? '50%' :
                                          analysisStage === 'generating' ? '75%' :
                                            analysisStage === 'complete' ? '100%' : '10%',
                                    }}
                                  >
                                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-50 animate-pulse"></div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </Card>
                        </>
                      ) : analysisError ? (
                        <Card className="p-8 text-center">
                          <AlertTriangle className="h-12 w-12 text-red-400 dark:text-red-600 mx-auto mb-4" />
                          <p className="text-gray-600 dark:text-slate-300 mb-2">
                            Failed to load analysis
                          </p>
                          <p className="text-sm text-gray-500 dark:text-slate-400">
                            {analysisErrorDetail instanceof Error ? analysisErrorDetail.message : 'An unexpected error occurred'}
                          </p>
                        </Card>
                      ) : analysis ? (
                        <div ref={analysisContentRef}>
                          <Card className="p-4 mb-4">
                            <div className="flex items-center justify-between mb-4">
                              <div className="flex items-center gap-2">
                                <h3 className="font-semibold text-theme-text">Capability Match Score</h3>
                                <div className="group relative inline-block">
                                  <button className="text-gray-400 hover:text-gray-600 dark:hover:text-slate-300 cursor-help">
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                    </svg>
                                  </button>
                                  <div className="pointer-events-none absolute left-0 mt-2 w-48 p-3 bg-gray-900 dark:bg-slate-700 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-20 whitespace-normal">
                                    <p className="font-semibold mb-1">
                                      {analysis.analysis_source === 'rag_analysis_quick'
                                        ? 'Quick Analysis (Limited Evidence)'
                                        : analysis.analysis_source === 'rag_analysis_deep'
                                          ? 'Deep Analysis (Comprehensive)'
                                          : 'Fallback Analysis (No Evidence)'}
                                    </p>
                                    {analysis.analysis_source === 'rag_analysis_quick' && (
                                      <p>Quick mode uses limited evidence for fast results. Score ranges from 0-100. Consider running Deep Analysis for more accurate matching.</p>
                                    )}
                                    {analysis.analysis_source === 'rag_analysis_deep' && (
                                      <p>Deep mode analyzes comprehensive evidence. Score reflects capability match based on available company documents.</p>
                                    )}
                                    {analysis.analysis_source === 'fallback' && (
                                      <p>No evidence available. Upload company documents (past projects, SOPs, staff info) to unlock capability analysis.</p>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="text-3xl font-bold text-primary-600 dark:text-primary-400">
                                  {(analysis.capability_match_score * 100).toFixed(0)}%
                                </div>
                                <div className="text-xs text-theme-text-muted mt-1">
                                  {analysis.capability_match_score >= 0.67 ? (
                                    <span className="text-green-600 dark:text-green-400 font-semibold">Strong Match</span>
                                  ) : analysis.capability_match_score >= 0.34 ? (
                                    <span className="text-yellow-600 dark:text-yellow-400 font-semibold">Moderate Match</span>
                                  ) : (
                                    <span className="text-red-600 dark:text-red-400 font-semibold">Low Match</span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4">
                              <div
                                className={`h-4 rounded-full transition-all ${analysis.capability_match_score >= 0.67
                                    ? 'bg-green-600'
                                    : analysis.capability_match_score >= 0.34
                                      ? 'bg-yellow-600'
                                      : 'bg-red-600'
                                  }`}
                                style={{ width: `${analysis.capability_match_score * 100}%` }}
                              />
                            </div>
                            <div className="mt-3 text-xs text-gray-600 dark:text-slate-400 space-y-1">
                              <p>Score Guide: <span className="text-green-600 dark:text-green-400">67-100% (Strong)</span> | <span className="text-yellow-600 dark:text-yellow-400">34-66% (Moderate)</span> | <span className="text-red-600 dark:text-red-400">0-33% (Low)</span></p>
                              <p className="text-gray-500 dark:text-slate-500">
                                {analysis.analysis_source === 'rag_analysis_quick' && '⚡ Quick mode: Use for initial screening. Run Deep Analysis for final decisions.'}
                                {analysis.analysis_source === 'rag_analysis_deep' && '📊 Deep mode: Comprehensive analysis based on available evidence.'}
                                {analysis.analysis_source === 'fallback' && '⚠️ Add company documents for accurate capability matching.'}
                              </p>
                            </div>

                            {/* Re-analyze buttons */}
                            <div className="mt-4 flex gap-2">
                              <button
                                onClick={handleReanalyzeQuick}
                                disabled={analysisLoading}
                                className="flex-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white text-sm font-medium rounded-lg transition-colors"
                              >
                                ⚡ Re-analyze (Quick)
                              </button>
                              <button
                                onClick={() => setShowDetailModeWarning(true)}
                                disabled={analysisLoading}
                                className="flex-1 px-3 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white text-sm font-medium rounded-lg transition-colors"
                              >
                                📊 Re-analyze (Deep)
                              </button>
                            </div>
                          </Card>

                          {analysis.summary && (
                            <Card className="p-4 mb-4">
                              <h3 className="font-semibold mb-3 text-theme-text">Analysis Summary</h3>
                              <div
                                className="text-gray-700 dark:text-slate-200 leading-relaxed prose prose-sm max-w-none"
                                dangerouslySetInnerHTML={{ __html: convertMarkdownToHTML(analysis.summary) }}
                              />
                            </Card>
                          )}

                          {analysis.technical_approaches && analysis.technical_approaches.length > 0 && (
                            <Card className="p-4 mb-4">
                              <h3 className="font-semibold mb-3 text-theme-text">Technical Approaches</h3>
                              <div className="space-y-3">
                                {analysis.technical_approaches.map((approach: any, idx: number) => (
                                  <div
                                    key={idx}
                                    className="p-3 border border-theme-border rounded-lg bg-blue-50 dark:bg-blue-900/20"
                                  >
                                    <div className="flex items-center justify-between mb-2">
                                      <span className="font-medium text-theme-text">
                                        {approach.title || 'Technical Approach'}
                                      </span>
                                      <Badge variant="default">
                                        {Math.min(100, Math.max(0, (approach.relevance_score || 0) * 100)).toFixed(1)}% relevant
                                      </Badge>
                                    </div>
                                    <p className="text-sm text-gray-700 dark:text-slate-300">
                                      {approach.description}
                                    </p>
                                    {approach.source && (
                                      <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                                        Source: {approach.source}
                                      </p>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </Card>
                          )}

                          {analysis.similar_projects && analysis.similar_projects.length > 0 && (
                            <Card className="p-4 mb-4">
                              <h3 className="font-semibold mb-3 text-theme-text">Similar Past Projects</h3>
                              <div className="space-y-3">
                                {analysis.similar_projects.map((project: any, idx: number) => (
                                  <div
                                    key={idx}
                                    className="p-3 border border-theme-border rounded-lg bg-gray-50 dark:bg-gray-800/50"
                                  >
                                    <div className="flex items-center justify-between mb-2">
                                      <span className="font-medium text-theme-text">
                                        {project.project_name}
                                      </span>
                                      <Badge variant="default">
                                        {Math.min(100, Math.max(0, (project.relevance_score || 0) * 100)).toFixed(1)}% match
                                      </Badge>
                                    </div>
                                    {project.similarity_reasons && project.similarity_reasons.length > 0 && (
                                      <ul className="text-sm text-gray-600 dark:text-slate-300 list-disc list-inside">
                                        {project.similarity_reasons.map((reason: string, i: number) => (
                                          <li key={i}>{reason}</li>
                                        ))}
                                      </ul>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </Card>
                          )}

                          {analysis.staff_allocation && analysis.staff_allocation.length > 0 && (
                            <Card className="p-4 mb-4">
                              <h3 className="font-semibold mb-3 text-theme-text">Recommended Staff Allocation</h3>
                              <div className="space-y-3">
                                {analysis.staff_allocation.map((staff: any, idx: number) => (
                                  <div
                                    key={idx}
                                    className="p-3 border border-theme-border rounded-lg bg-purple-50 dark:bg-purple-900/20"
                                  >
                                    <div className="flex items-center justify-between mb-2">
                                      <div>
                                        <span className="font-medium text-theme-text">
                                          {staff.name}
                                        </span>
                                        {staff.role && (
                                          <span className="text-sm text-gray-600 dark:text-slate-300 ml-2">
                                            - {staff.role}
                                          </span>
                                        )}
                                      </div>
                                      <Badge variant="default">
                                        {Math.min(100, Math.max(0, (staff.match_score || 0) * 100)).toFixed(1)}% match
                                      </Badge>
                                    </div>
                                    {staff.qualifications && staff.qualifications.length > 0 && (
                                      <div className="flex flex-wrap gap-1 mt-2">
                                        {staff.qualifications.map((qual: string, i: number) => (
                                          <Badge key={i} variant="default" className="text-xs">
                                            {qual}
                                          </Badge>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </Card>
                          )}

                          {analysis.recommendations && analysis.recommendations.length > 0 && (
                            <Card className="p-4 mb-4">
                              <h3 className="font-semibold mb-3 text-theme-text">Strategic Recommendations</h3>
                              <div
                                className="text-gray-700 dark:text-slate-300 leading-relaxed"
                                dangerouslySetInnerHTML={{ __html: convertMarkdownToHTML(analysis.recommendations.join('\n\n')) }}
                              />
                            </Card>
                          )}

                          {/* Enhanced Citations Section with Artifact Details */}
                          {analysis.citations && analysis.citations.length > 0 && (
                            <Card className="p-4 mb-4">
                              <h3 className="text-base font-bold text-theme-text mb-4 flex items-center gap-2">
                                <FileText className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                                Evidence Sources ({analysis.citations.length})
                              </h3>
                              <div className="space-y-3 max-h-96 overflow-y-auto">
                                {analysis.citations.map((citation: any, idx: number) => {
                                  const isExpanded = expandedCitation === idx;
                                  // Stable id for this Evidence Source card; [Source N] links scroll to evidence-source-{displayNum}
                                  const displayNum = citation.id ?? citation.rank ?? idx + 1;
                                  return (
                                    <div
                                      key={citation.id || idx}
                                      id={`evidence-source-${displayNum}`}
                                      className="bg-gradient-to-r from-white to-primary-50 dark:from-gray-800 dark:to-primary-900/20 rounded-lg border-2 border-primary-200 dark:border-primary-700 hover:border-primary-400 dark:hover:border-primary-600 transition-all shadow-sm hover:shadow-md scroll-mt-4"
                                    >
                                      {/* Citation Header */}
                                      <div className="p-4">
                                        <div className="flex items-start gap-3">
                                          <div className="flex-shrink-0">
                                            <div className="w-8 h-8 rounded-full bg-primary-600 dark:bg-primary-500 text-white flex items-center justify-center font-bold text-sm">
                                              {citation.id || citation.rank || idx + 1}
                                            </div>
                                          </div>

                                          <div className="flex-1 min-w-0">
                                            {/* Source Document Info */}
                                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                                              <FileText className="h-4 w-4 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                                              <span className="text-sm font-bold text-theme-text truncate" title={citation.artifact_name || citation.artifact_filename || citation.source || citation.artifact_id || 'Unknown Document'}>
                                                {(() => {
                                                  // Try multiple fields to get the document name (artifact_filename is from RAG response)
                                                  const name = citation.artifact_name ||
                                                    (citation as any).artifact_filename ||
                                                    citation.source ||
                                                    (citation.artifact_id ? `Document ${citation.artifact_id.substring(0, 8)}` : null) ||
                                                    (citation.evidence_id ? `Evidence ${citation.evidence_id.substring(0, 8)}` : null) ||
                                                    (citation.chunk_id ? `Chunk ${citation.chunk_id.substring(0, 8)}` : null) ||
                                                    'Unknown Document';
                                                  return name;
                                                })()}
                                              </span>
                                              {citation.confidence && (
                                                <Badge variant="default" className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200 text-xs">
                                                  {Math.min(100, Math.max(0, (citation.confidence * 100))).toFixed(1)}% match
                                                </Badge>
                                              )}
                                              {/* {citation.relevance_score && (
                                          <Badge variant="default" className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200 text-xs">
                                            {Math.min(100, Math.max(0, (citation.relevance_score * 100))).toFixed(1)}% relevant
                                          </Badge>
                                        )} */}
                                            </div>

                                            {/* Metadata Tags */}
                                            <div className="flex items-center gap-2 mb-2 flex-wrap text-xs">
                                              {citation.page && (
                                                <span className="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200 rounded-md font-medium">
                                                  📄 Page {citation.page}
                                                </span>
                                              )}
                                              {citation.section && (
                                                <span className="px-2 py-1 bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-200 rounded-md font-medium">
                                                  📑 {citation.section}
                                                </span>
                                              )}
                                              {citation.section_path && (
                                                <span className="px-2 py-1 bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-200 rounded-md font-medium">
                                                  📑 {citation.section_path}
                                                </span>
                                              )}
                                              {citation.chunk_id && (
                                                <span className="px-2 py-1 bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-200 rounded-md font-mono text-xs">
                                                  ID: {citation.chunk_id.substring(0, 8)}...
                                                </span>
                                              )}
                                              {citation.rank && (
                                                <span className="px-2 py-1 bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200 rounded-md font-medium">
                                                  Rank: {citation.rank}
                                                </span>
                                              )}
                                            </div>

                                            {/* Preview Content */}
                                            {citation.content && (
                                              <div className="mt-3 p-3 bg-white dark:bg-gray-800 rounded-md border border-primary-100 dark:border-primary-800">
                                                <div className="text-xs font-semibold text-primary-700 dark:text-primary-300 mb-2 flex items-center gap-1">
                                                  <span>📝</span>
                                                  <span>Relevant Excerpt:</span>
                                                </div>
                                                <div className={`text-sm text-theme-text leading-relaxed ${!isExpanded ? 'line-clamp-3' : ''}`}>
                                                  "{citation.content}"
                                                </div>
                                                {citation.content.length > 200 && (
                                                  <button
                                                    onClick={() => setExpandedCitation(isExpanded ? null : idx)}
                                                    className="mt-2 text-xs text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 font-medium flex items-center gap-1"
                                                  >
                                                    {isExpanded ? (
                                                      <>
                                                        <ChevronDown className="h-3 w-3" />
                                                        Show less
                                                      </>
                                                    ) : (
                                                      <>
                                                        <ChevronRight className="h-3 w-3" />
                                                        Read more
                                                      </>
                                                    )}
                                                  </button>
                                                )}
                                              </div>
                                            )}

                                            {/* Action Buttons */}
                                            <div className="mt-3 flex items-center gap-2">
                                              {citation.artifact_id && (
                                                <Button
                                                  size="sm"
                                                  variant="outline"
                                                  onClick={() => {
                                                    // Navigate to Knowledge Base with this artifact
                                                    window.location.href = `/knowledge?artifact=${citation.artifact_id}`;
                                                  }}
                                                  className="text-xs"
                                                >
                                                  <FileText className="h-3 w-3 mr-1" />
                                                  View Document
                                                </Button>
                                              )}
                                              {citation.chunk_id && (
                                                <Button
                                                  size="sm"
                                                  variant="outline"
                                                  onClick={() => {
                                                    // Navigate to Evidence Viewer with this chunk
                                                    window.location.href = `/evidence?chunk=${citation.chunk_id}`;
                                                  }}
                                                  className="text-xs"
                                                >
                                                  <Search className="h-3 w-3 mr-1" />
                                                  View Chunk
                                                </Button>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>

                              {/* Help Text */}
                              <div className="mt-4 p-3 bg-primary-50 dark:bg-primary-900/20 rounded-lg border border-primary-200 dark:border-primary-700">
                                <p className="text-xs text-primary-700 dark:text-primary-300">
                                  💡 <span className="font-semibold">Tip:</span> Click "View Document" to see the full source file, or "View Chunk" to explore the specific evidence section.
                                </p>
                              </div>
                            </Card>
                          )}
                        </div>
                      ) : (
                        <Card className="p-8 text-center">
                          <AlertTriangle className="h-12 w-12 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
                          <p className="text-gray-600 dark:text-slate-300">
                            Analysis not available. Please try again later.
                          </p>
                        </Card>
                      )}
                    </div>
                  ),
                },
                {
                  id: 'email-draft',
                  label: '5. Email Draft',
                  title: 'Draft your response or outreach email for this opportunity.',
                  content: (
                    <OpportunityEmailDraftTab
                      opportunityId={opportunity.id}
                      projectId={projectId || ''}
                      opportunity={enhancedOpp}
                      sourcePostUrl={viewUrl}
                    />
                  ),
                },
              ]}
              defaultTab={activeTab}
              onChange={(tabId) => {
                setActiveTab(tabId as any);
                // Trigger analysis fetch when Analysis tab is opened
                if (tabId === 'analysis' && opportunity?.id) {
                  // Query will auto-fetch due to enabled condition
                }
              }}
            />
          </div>
        </div>
      </div>

      {/* Detail Mode Warning Modal */}
      {showDetailModeWarning && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 z-[9999] flex items-center justify-center p-4">
          <Card className="w-full max-w-md p-6 bg-theme-paper">
            <div className="mb-4">
              <h3 className="text-lg font-bold text-theme-text mb-2">Deep Analysis Required</h3>
              <p className="text-sm text-gray-600 dark:text-slate-300">
                Deep analysis provides comprehensive capability matching by analyzing all available company documents and evidence.
              </p>
            </div>

            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-3 mb-4">
              <p className="text-sm text-yellow-800 dark:text-yellow-200 font-semibold mb-1">⏱️ Processing Time</p>
              <p className="text-sm text-yellow-700 dark:text-yellow-300">
                This analysis may take up to <strong>5 minutes</strong> to complete. Please keep this window open during analysis.
              </p>
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-3 mb-4">
              <p className="text-sm text-blue-800 dark:text-blue-200 font-semibold mb-1">📊 What You'll Get</p>
              <ul className="text-sm text-blue-700 dark:text-blue-300 list-disc list-inside space-y-1">
                <li>Technical approaches matched to this opportunity</li>
                <li>Similar past projects from your database</li>
                <li>Recommended staff allocation</li>
                <li>Strategic recommendations</li>
              </ul>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setShowDetailModeWarning(false)}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-300 font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleReanalyzeDetail}
                className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors"
              >
                Start Deep Analysis
              </button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );

  // Render modal using portal to body to avoid z-index issues
  if (typeof document === 'undefined') {
    // console.log('[OpportunityDetailModal] document is undefined, cannot render portal');
    return null;
  }
  // console.log('[OpportunityDetailModal] Rendering portal to document.body');
  return createPortal(modalContent, document.body);
}


